import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Inbox,
  Info,
  Loader2,
  Mail,
  Phone,
  RotateCcw,
  Search,
} from 'lucide-react';
import { api } from '../../lib/api';
import { pickLoc } from '../../lib/collections';
import type {
  CollectionTypeDef,
  FormSubmission,
  SubmissionStats,
} from '../../lib/types';
import ContextCard from '../../components/ui/ContextCard';
import PageContainer from '../../components/ui/PageContainer';
import PageHeader from '../../components/ui/PageHeader';

const FIELD_LABELS: Record<string, string> = {
  name: 'Nombre',
  nombre: 'Nombre',
  email: 'Correo',
  phone: 'Teléfono',
  telefono: 'Teléfono',
  message: 'Mensaje',
  mensaje: 'Mensaje',
};

interface SubmissionContext {
  industryLabel: string;
  collectionTypes: CollectionTypeDef[];
}

type StatusFilter = 'all' | 'pending' | 'attended';
type SortOrder = 'newest' | 'oldest' | 'pending-first';

function formatDate(val: Date | string | number) {
  const date = new Date(val);
  return date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
}

function parsePayload(submission: FormSubmission): Record<string, unknown> {
  try {
    return JSON.parse(submission.payloadJson) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function textValue(payload: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function relatedCollection(formId: string, collectionTypes: CollectionTypeDef[]) {
  const normalized = formId.toLocaleLowerCase('es');
  return collectionTypes.find((type) => {
    const route = pickLoc(type.route, 'es').toLocaleLowerCase('es');
    return type.key.toLocaleLowerCase('es') === normalized
      || type.key.toLocaleLowerCase('es') === `${normalized}s`
      || route === normalized;
  });
}

function formLabel(formId: string, context: SubmissionContext) {
  if (formId === 'contact') return 'Formulario de contacto';
  const collection = relatedCollection(formId, context.collectionTypes);
  const singular = collection ? pickLoc(collection.labels.singular, 'es') : '';
  if (singular) return `Interés en ${singular.toLocaleLowerCase('es')}`;
  return 'Formulario del sitio';
}

function fieldLabel(key: string, relatedLabel: string) {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  if (relatedLabel && key.toLocaleLowerCase('es').endsWith('title')) return `${relatedLabel} de interés`;
  if (relatedLabel && key.toLocaleLowerCase('es').endsWith('slug')) return `Referencia de ${relatedLabel.toLocaleLowerCase('es')}`;
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (letter) => letter.toUpperCase());
}

function PayloadRow({
  submission,
  context,
  updating,
  onSetAttended,
}: {
  submission: FormSubmission;
  context: SubmissionContext;
  updating: boolean;
  onSetAttended: (submission: FormSubmission, attended: boolean) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const payload = parsePayload(submission);
  const entries = Object.entries(payload).filter(([, value]) => value !== undefined && value !== null && value !== '');
  const name = textValue(payload, 'nombre', 'name') || 'Consulta sin nombre';
  const email = textValue(payload, 'email');
  const phone = textValue(payload, 'phone', 'telefono');
  const message = textValue(payload, 'message', 'mensaje');
  const collection = relatedCollection(submission.formId, context.collectionTypes);
  const relatedLabel = collection ? pickLoc(collection.labels.singular, 'es') : '';
  const relatedTitle = Object.entries(payload).find(([key, value]) => (
    key.toLocaleLowerCase('es').endsWith('title') && typeof value === 'string' && value.trim()
  ))?.[1] as string | undefined;

  return (
    <article className="border-b border-ui-border last:border-0">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="w-full flex items-start gap-3 px-4 py-4 hover:bg-ui-text/[0.04] text-left transition-colors"
      >
        <span className="text-ui-text-subtle w-4 shrink-0 mt-1">
          {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-ui-text truncate">{name}</p>
              <p className="text-sm text-ui-text-subtle truncate">{formLabel(submission.formId, context)}</p>
              {(relatedTitle || message) && (
                <p className="mt-1 text-sm text-ui-text-muted truncate">
                  {relatedTitle ? `${relatedTitle}${message ? ` · ${message}` : ''}` : message}
                </p>
              )}
              <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-sm text-ui-text-subtle">
                {email && <span className="truncate">{email}</span>}
                {phone && <span>{phone}</span>}
              </div>
            </div>
            <span className={`shrink-0 text-sm font-medium ${submission.attendedAt ? 'text-ui-success' : 'text-ui-warning'}`}>
              {submission.attendedAt ? 'Atendida' : 'Pendiente'}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-ui-text-subtle">
            <span>{formatDate(submission.createdAt)}</span>
            <span className={submission.emailSent ? 'text-ui-accent' : ''}>
              {submission.emailSent ? 'Aviso enviado' : 'Guardada en el panel'}
            </span>
          </div>
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4">
          <div className="rounded-lg border border-ui-border bg-ui-bg p-4 space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <button
                type="button"
                disabled={updating}
                onClick={() => onSetAttended(submission, !submission.attendedAt)}
                className={`inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
                  submission.attendedAt
                    ? 'border border-ui-border-strong text-ui-text hover:bg-ui-text/[0.05]'
                    : 'bg-ui-accent text-ui-bg hover:bg-ui-accent-hover'
                }`}
              >
                {updating ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : submission.attendedAt ? (
                  <RotateCcw size={15} />
                ) : (
                  <CheckCircle2 size={15} />
                )}
                {submission.attendedAt ? 'Marcar como pendiente' : 'Marcar como atendida'}
              </button>
              {email && (
                <a
                  href={`mailto:${email}`}
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-md bg-ui-accent px-3 py-2 text-sm font-medium text-ui-bg hover:bg-ui-accent-hover transition-colors"
                >
                  <Mail size={15} /> Responder por correo
                </a>
              )}
              {phone && (
                <a
                  href={`tel:${phone}`}
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-md border border-ui-border-strong px-3 py-2 text-sm font-medium text-ui-text hover:bg-ui-text/[0.05] transition-colors"
                >
                  <Phone size={15} /> Llamar
                </a>
              )}
            </div>

            {!submission.emailSent && (
              <div className="flex gap-2 rounded-md border border-ui-border bg-ui-surface px-3 py-2.5 text-sm text-ui-text-muted">
                <Info size={16} className="mt-0.5 shrink-0 text-ui-accent" />
                <p>La consulta quedó guardada aquí, aunque no se envió un aviso por correo al negocio.</p>
              </div>
            )}

            {entries.length > 0 ? (
              <dl className="grid gap-3 sm:grid-cols-[9rem_1fr] text-sm">
                {entries.map(([key, value]) => (
                  <div key={key} className="contents">
                    <dt className="text-ui-text-subtle">{fieldLabel(key, relatedLabel)}</dt>
                    <dd className="text-ui-text whitespace-pre-wrap break-words">{String(value)}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-sm text-ui-text-muted">Esta consulta no contiene datos que se puedan mostrar.</p>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

export default function SubmissionsPage() {
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [stats, setStats] = useState<SubmissionStats | null>(null);
  const [context, setContext] = useState<SubmissionContext>({ industryLabel: '', collectionTypes: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
  const [updatingIds, setUpdatingIds] = useState<Set<number>>(new Set());

  function loadSubmissions() {
    setLoading(true);
    setError('');
    Promise.all([
      api.submissions.list(),
      api.submissions.stats().catch(() => null),
      api.collectionTypes.list().catch(() => []),
      api.onboarding.catalog().catch(() => null),
    ])
      .then(([items, submissionStats, collectionTypes, catalog]) => {
        setSubmissions(items);
        setStats(submissionStats);
        const industry = catalog?.industries.find((item) => item.key === catalog.industry);
        setContext({
          industryLabel: industry ? pickLoc(industry.label, 'es') : '',
          collectionTypes,
        });
      })
      .catch(() => setError('No pudimos cargar las consultas. Intenta de nuevo en un momento.'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadSubmissions();
    api.submissions.markRead()
      .then(() => window.dispatchEvent(new Event('submissions:read')))
      .catch(() => { /* The badge will update during the next poll. */ });
  }, []);

  async function handleSetAttended(submission: FormSubmission, attended: boolean) {
    setActionError('');
    setUpdatingIds((current) => new Set(current).add(submission.id));
    try {
      const result = await api.submissions.setAttended(submission.id, attended);
      setSubmissions((current) => current.map((item) => (
        item.id === submission.id ? { ...item, attendedAt: result.attendedAt } : item
      )));
    } catch {
      setActionError('No pudimos actualizar la consulta. Intenta de nuevo.');
    } finally {
      setUpdatingIds((current) => {
        const next = new Set(current);
        next.delete(submission.id);
        return next;
      });
    }
  }

  const filteredSubmissions = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('es');
    const filtered = submissions.filter((submission) => {
      if (statusFilter === 'pending' && submission.attendedAt) return false;
      if (statusFilter === 'attended' && !submission.attendedAt) return false;
      if (!term) return true;
      const payload = parsePayload(submission);
      return `${formLabel(submission.formId, context)} ${Object.values(payload).join(' ')}`
        .toLocaleLowerCase('es')
        .includes(term);
    });
    return filtered.sort((left, right) => {
      if (sortOrder === 'pending-first') {
        const statusDifference = Number(Boolean(left.attendedAt)) - Number(Boolean(right.attendedAt));
        if (statusDifference !== 0) return statusDifference;
      }
      const difference = new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
      return sortOrder === 'oldest' ? -difference : difference;
    });
  }, [context, search, sortOrder, statusFilter, submissions]);

  const pendingCount = submissions.filter((submission) => !submission.attendedAt).length;

  return (
    <PageContainer>
      <PageHeader title="Consultas del sitio" />

      <ContextCard
        title="¿Qué es una consulta?"
        description="Es un mensaje o solicitud que una persona envió desde un formulario de tu sitio, por ejemplo para pedir información, cotizar o mostrar interés en algo."
        detail="Abre cada consulta para ver todos sus datos. Márcala como atendida cuando ya hayas respondido o dado seguimiento; esto no envía una respuesta automáticamente."
      />

      {!loading && !error && submissions.length > 0 && (
        <section className="grid gap-3 sm:grid-cols-3" aria-label="Resumen de consultas">
          <div className="rounded-lg border border-ui-border bg-ui-surface px-4 py-3">
            <p className="text-xs text-ui-text-subtle">Total recibido</p>
            <p className="mt-1 text-2xl font-semibold text-ui-text">{stats?.total ?? submissions.length}</p>
          </div>
          <div className="rounded-lg border border-ui-border bg-ui-surface px-4 py-3">
            <p className="text-xs text-ui-text-subtle">Este mes</p>
            <p className="mt-1 text-2xl font-semibold text-ui-text">{stats?.thisMonth ?? 0}</p>
          </div>
          <div className="rounded-lg border border-ui-border bg-ui-surface px-4 py-3">
            <p className="text-xs text-ui-text-subtle">Pendientes de atender</p>
            <p className="mt-1 text-2xl font-semibold text-ui-warning">{pendingCount}</p>
          </div>
        </section>
      )}

      {loading ? (
        <div className="flex justify-center rounded-lg border border-ui-border bg-ui-surface py-16">
          <div className="w-6 h-6 border-2 border-ui-accent border-t-transparent rounded-full animate-spin" aria-label="Cargando consultas" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-ui-border bg-ui-surface px-6 py-8 text-center">
          <AlertTriangle size={24} className="mx-auto text-ui-accent" />
          <p className="mt-3 text-sm text-ui-text">{error}</p>
          <button type="button" onClick={loadSubmissions} className="mt-4 text-sm font-medium text-ui-accent hover:underline">
            Volver a intentar
          </button>
        </div>
      ) : submissions.length === 0 ? (
        <div className="bg-ui-surface border border-ui-border rounded-lg px-6 py-12 text-center">
          <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-ui-accent/10 text-ui-accent">
            <Inbox size={21} />
          </span>
          <h2 className="mt-4 text-base font-semibold text-ui-text">Aún no hay consultas</h2>
          <p className="mx-auto mt-2 max-w-lg text-sm text-ui-text-muted">
            Cuando una persona complete un formulario en tu sitio, su nombre, datos de contacto y mensaje aparecerán aquí automáticamente.
          </p>
          <p className="mx-auto mt-3 max-w-lg text-xs text-ui-text-subtle">
            Los formularios pueden estar en tu página de contacto o en las secciones de contenido publicadas para tu negocio.
          </p>
        </div>
      ) : (
        <section className="space-y-3">
          {actionError && (
            <div className="flex gap-2 rounded-lg border border-ui-border bg-ui-surface px-4 py-3 text-sm text-ui-text-muted">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-ui-warning" />
              <p>{actionError}</p>
            </div>
          )}

          <div className="grid gap-2 md:grid-cols-[minmax(240px,1fr)_170px_190px]">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-text-subtle pointer-events-none" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar por nombre, correo, teléfono o mensaje"
                className="w-full rounded-lg border border-ui-border bg-ui-surface py-2.5 pl-9 pr-3 text-sm text-ui-text outline-none placeholder:text-ui-text-subtle focus:border-ui-accent"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
              aria-label="Filtrar consultas por estado"
              className="rounded-lg border border-ui-border bg-ui-surface px-3 py-2.5 text-sm text-ui-text outline-none focus:border-ui-accent"
            >
              <option value="all">Todos los estados</option>
              <option value="pending">Pendientes</option>
              <option value="attended">Atendidas</option>
            </select>
            <select
              value={sortOrder}
              onChange={(event) => setSortOrder(event.target.value as SortOrder)}
              aria-label="Ordenar consultas"
              className="rounded-lg border border-ui-border bg-ui-surface px-3 py-2.5 text-sm text-ui-text outline-none focus:border-ui-accent"
            >
              <option value="newest">Más recientes primero</option>
              <option value="oldest">Más antiguas primero</option>
              <option value="pending-first">Pendientes primero</option>
            </select>
          </div>

          <div className="bg-ui-surface border border-ui-border rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 flex items-center justify-between gap-3 border-b border-ui-border">
              <span className="text-xs text-ui-text-subtle">
                {filteredSubmissions.length} {filteredSubmissions.length === 1 ? 'consulta visible' : 'consultas visibles'}
              </span>
              <span className="text-xs text-ui-text-subtle">{pendingCount} pendientes en total</span>
            </div>
            {filteredSubmissions.length > 0 ? (
              filteredSubmissions.map((submission) => (
                <PayloadRow
                  key={submission.id}
                  submission={submission}
                  context={context}
                  updating={updatingIds.has(submission.id)}
                  onSetAttended={handleSetAttended}
                />
              ))
            ) : (
              <div className="px-6 py-10 text-center">
                <p className="text-sm font-medium text-ui-text">No encontramos coincidencias</p>
                <p className="mt-1 text-sm text-ui-text-subtle">Prueba con otro nombre, correo, teléfono o palabra del mensaje.</p>
              </div>
            )}
          </div>
        </section>
      )}
    </PageContainer>
  );
}
